/**
 * ========================================
 * VOBVOROT HYBRID SPA SYSTEM
 * ========================================
 * Обеспечивает бесшовную навигацию между страницами без перезагрузки
 *
 * ПРЕИМУЩЕСТВА:
 * - Музыкальный плеер НЕ прерывается (0 секунд паузы)
 * - Плавные transition анимации между страницами
 * - Сохранение состояния header, footer, sidebar
 * - Меньше загрузки данных (только main контент)
 * - App-like UX опыт
 *
 * СОВМЕСТИМОСТЬ:
 * - ✅ Theme Editor продолжает работать
 * - ✅ SEO не страдает (server-side rendered)
 * - ✅ Shopify Checkout работает нормально (full reload)
 * - ✅ Все Shopify apps совместимы
 *
 * АРХИТЕКТУРА:
 * - Modular design с 8 классами
 * - Event-driven с custom events
 * - Graceful degradation (fallback на full reload)
 * - Error handling на каждом этапе
 */

(function() {
  'use strict';

  // ========================================
  // CONFIGURATION
  // ========================================
  const SPA_CONFIG = {
    // Пути которые должны делать full reload (не AJAX)
    excludedPaths: [
      '/checkout',
      '/cart',
      '/account',
      '/admin',
      '/apps',
      '/tools'
    ],

    // Селекторы ссылок которые должны открываться нормально
    excludedSelectors: [
      'a[target="_blank"]',
      'a[href^="mailto:"]',
      'a[href^="tel:"]',
      'a[href^="http://"]', // External links
      'a[href^="https://"]', // External links (except our domain)
      'a[download]',
      'a[href^="#"]' // Anchor links
    ],

    // Transition настройки
    transitionDuration: 300, // ms
    scrollBehavior: 'instant', // or 'smooth'

    // Debug mode
    debug: true,

    // Prefetch настройки
    enablePrefetch: true,
    prefetchDelay: 100 // ms на hover
  };

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================
  const Utils = {
    log(...args) {
      if (SPA_CONFIG.debug) {
        console.log('[Hybrid SPA]', ...args);
      }
    },

    error(...args) {
      console.error('[Hybrid SPA ERROR]', ...args);
    },

    // Проверка является ли URL внутренним
    isInternalUrl(url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        return urlObj.origin === window.location.origin;
      } catch (e) {
        return false;
      }
    },

    // Проверка является ли путь исключенным
    isExcludedPath(url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        const pathname = urlObj.pathname;

        return SPA_CONFIG.excludedPaths.some(excluded =>
          pathname.startsWith(excluded)
        );
      } catch (e) {
        return true; // На всякий случай делаем full reload
      }
    },

    // Delay helper
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // Dispatch custom event
    dispatchEvent(name, detail = {}) {
      const event = new CustomEvent(name, { detail, bubbles: true });
      document.dispatchEvent(event);
    }
  };

  // ========================================
  // STATE MANAGER
  // ========================================
  class SPAStateManager {
    constructor() {
      this.scrollPositions = new Map();
      this.isTransitioning = false;
      this.currentUrl = window.location.href;
      this.prefetchCache = new Map();
    }

    saveScrollPosition(url) {
      this.scrollPositions.set(url, {
        x: window.scrollX,
        y: window.scrollY
      });
      Utils.log('Scroll position saved for', url, '→', window.scrollY);
    }

    restoreScrollPosition(url) {
      const position = this.scrollPositions.get(url);
      if (position) {
        window.scrollTo(position.x, position.y);
        Utils.log('Scroll position restored for', url, '→', position.y);
      } else {
        window.scrollTo(0, 0);
      }
    }

    clearScrollPositions() {
      // Храним только последние 20 позиций для экономии памяти
      if (this.scrollPositions.size > 20) {
        const entries = Array.from(this.scrollPositions.entries());
        this.scrollPositions = new Map(entries.slice(-20));
      }
    }

    cachePage(url, doc) {
      this.prefetchCache.set(url, {
        doc,
        timestamp: Date.now()
      });

      // Чистим старый кэш (старше 5 минут)
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      for (const [cachedUrl, data] of this.prefetchCache.entries()) {
        if (data.timestamp < fiveMinutesAgo) {
          this.prefetchCache.delete(cachedUrl);
        }
      }
    }

    getCachedPage(url) {
      const cached = this.prefetchCache.get(url);
      if (cached) {
        Utils.log('Using cached page for', url);
        return cached.doc;
      }
      return null;
    }
  }

  // ========================================
  // LINK INTERCEPTOR
  // ========================================
  class LinkInterceptor {
    constructor(onNavigate, onPrefetch) {
      this.onNavigate = onNavigate;
      this.onPrefetch = onPrefetch;
      this.prefetchTimeout = null;
      this.init();
    }

    init() {
      // Click event delegation
      document.addEventListener('click', this.handleClick.bind(this), true);

      // Hover prefetch (optional)
      if (SPA_CONFIG.enablePrefetch) {
        document.addEventListener('mouseover', this.handleHover.bind(this), true);
        document.addEventListener('mouseout', this.handleMouseOut.bind(this), true);
      }

      Utils.log('Link interceptor initialized');
    }

    handleClick(e) {
      const link = e.target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');

      // Проверки
      if (!this.shouldIntercept(link, href)) {
        Utils.log('Link not intercepted:', href);
        return;
      }

      // Перехватываем клик
      e.preventDefault();
      e.stopPropagation();

      Utils.log('Link intercepted:', href);
      this.onNavigate(href);
    }

    handleHover(e) {
      const link = e.target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');

      if (!this.shouldIntercept(link, href)) return;

      // Prefetch с задержкой (чтобы не prefetch'ить случайные hovers)
      this.prefetchTimeout = setTimeout(() => {
        Utils.log('Prefetching:', href);
        this.onPrefetch(href);
      }, SPA_CONFIG.prefetchDelay);
    }

    handleMouseOut() {
      if (this.prefetchTimeout) {
        clearTimeout(this.prefetchTimeout);
        this.prefetchTimeout = null;
      }
    }

    shouldIntercept(link, href) {
      // Проверка 1: href существует
      if (!href || href === '#') return false;

      // Проверка 2: не является excluded selector
      for (const selector of SPA_CONFIG.excludedSelectors) {
        if (link.matches(selector)) {
          // Исключение для internal https ссылок
          if (selector.includes('https://') && Utils.isInternalUrl(href)) {
            continue; // Не исключаем internal https
          }
          return false;
        }
      }

      // Проверка 3: internal URL
      if (!Utils.isInternalUrl(href)) return false;

      // Проверка 4: не excluded path
      if (Utils.isExcludedPath(href)) return false;

      // Проверка 5: не тот же URL
      const currentUrl = window.location.href.split('#')[0];
      const targetUrl = new URL(href, window.location.origin).href.split('#')[0];
      if (currentUrl === targetUrl) return false;

      return true;
    }
  }

  // ========================================
  // PAGE LOADER
  // ========================================
  class PageLoader {
    constructor(stateManager) {
      this.stateManager = stateManager;
      this.abortController = null;
    }

    async fetchPage(url) {
      try {
        // Проверяем кэш
        const cached = this.stateManager.getCachedPage(url);
        if (cached) {
          return cached;
        }

        // Отменяем предыдущий запрос если есть
        if (this.abortController) {
          this.abortController.abort();
        }

        this.abortController = new AbortController();

        Utils.log('Fetching page:', url);

        const response = await fetch(url, {
          signal: this.abortController.signal,
          credentials: 'same-origin', // ВАЖНО: отправлять cookies (включая localization)
          headers: {
            'X-Requested-With': 'XMLHttpRequest' // Shopify может использовать это
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const html = await response.text();
        const doc = this.parsePage(html);

        // Кэшируем
        this.stateManager.cachePage(url, doc);

        Utils.log('Page fetched and parsed:', url);
        return doc;

      } catch (error) {
        if (error.name === 'AbortError') {
          Utils.log('Fetch aborted');
          return null;
        }

        Utils.error('Failed to fetch page:', error);
        throw error;
      }
    }

    parsePage(html) {
      const parser = new DOMParser();
      return parser.parseFromString(html, 'text/html');
    }

    // Prefetch (без ожидания результата)
    prefetchPage(url) {
      // Проверяем не в кэше ли уже
      if (this.stateManager.getCachedPage(url)) {
        return;
      }

      // Prefetch в фоне
      this.fetchPage(url).catch(err => {
        Utils.log('Prefetch failed (non-critical):', err.message);
      });
    }
  }

  // ========================================
  // CONTENT REPLACER
  // ========================================
  class ContentReplacer {
    replaceContent(newDoc) {
      try {
        // 1. Replace <main> content
        this.replaceMain(newDoc);

        // 2. Update <title>
        this.updateTitle(newDoc);

        // 3. Update meta tags
        this.updateMetaTags(newDoc);

        // 4. Update canonical URL
        this.updateCanonical(newDoc);

        // 5. Update body classes (для page-specific styles)
        this.updateBodyClasses(newDoc);

        Utils.log('Content replaced successfully');
        Utils.dispatchEvent('spa:contentReplaced', { doc: newDoc });

      } catch (error) {
        Utils.error('Failed to replace content:', error);
        throw error;
      }
    }

    replaceMain(newDoc) {
      const oldMain = document.querySelector('main');
      const newMain = newDoc.querySelector('main');

      if (!oldMain || !newMain) {
        throw new Error('Main element not found');
      }

      // Clone чтобы не терять reference
      const newMainClone = newMain.cloneNode(true);

      // Reset opacity для нового контента
      newMainClone.style.opacity = '0';

      // Replace
      oldMain.replaceWith(newMainClone);

      Utils.log('Main content replaced');
    }

    updateTitle(newDoc) {
      const newTitle = newDoc.querySelector('title');
      if (newTitle) {
        document.title = newTitle.textContent;
        Utils.log('Title updated:', document.title);
      }
    }

    updateMetaTags(newDoc) {
      // Update description
      const newDescription = newDoc.querySelector('meta[name="description"]');
      const oldDescription = document.querySelector('meta[name="description"]');

      if (newDescription && oldDescription) {
        oldDescription.setAttribute('content', newDescription.getAttribute('content'));
      }

      // Update Open Graph tags
      const ogTags = ['og:title', 'og:description', 'og:image', 'og:url'];
      ogTags.forEach(property => {
        const newTag = newDoc.querySelector(`meta[property="${property}"]`);
        const oldTag = document.querySelector(`meta[property="${property}"]`);

        if (newTag && oldTag) {
          oldTag.setAttribute('content', newTag.getAttribute('content'));
        }
      });

      Utils.log('Meta tags updated');
    }

    updateCanonical(newDoc) {
      const newCanonical = newDoc.querySelector('link[rel="canonical"]');
      const oldCanonical = document.querySelector('link[rel="canonical"]');

      if (newCanonical && oldCanonical) {
        oldCanonical.setAttribute('href', newCanonical.getAttribute('href'));
        Utils.log('Canonical updated:', newCanonical.getAttribute('href'));
      }
    }

    updateBodyClasses(newDoc) {
      const newBody = newDoc.querySelector('body');
      const currentBody = document.querySelector('body');

      if (newBody && currentBody) {
        // Сохраняем существующие utility классы
        const preserveClasses = ['transitioning', 'spa-active'];
        const currentUtilityClasses = Array.from(currentBody.classList).filter(cls =>
          preserveClasses.includes(cls)
        );

        // Применяем новые классы + восстанавливаем utility
        currentBody.className = newBody.className;
        currentUtilityClasses.forEach(cls => currentBody.classList.add(cls));

        Utils.log('Body classes updated');
      }
    }
  }

  // ========================================
  // SCRIPT REINITIALIZER
  // ========================================
  class ScriptReinitializer {
    reinitialize(newDoc) {
      try {
        Utils.log('Reinitializing scripts...');

        // 1. FIRST: Execute inline scripts (sets window.productJSON and other data)
        this.executeInlineScripts();

        // 2. Product page scripts (needs productJSON from inline scripts)
        if (this.isProductPage()) {
          this.initProductPage();
        }

        // 3. Collection page scripts
        if (this.isCollectionPage()) {
          this.initCollectionPage();
        }

        // 4. Trigger custom event for SPA-aware scripts
        this.triggerDOMContentLoaded();

        Utils.log('Scripts reinitialized');
        Utils.dispatchEvent('spa:scriptsReinitialized');

      } catch (error) {
        Utils.error('Failed to reinitialize scripts:', error);
        // Не бросаем ошибку - non-critical
      }
    }

    isProductPage() {
      // Check for product page elements - use actual class names from product-page-unified.liquid
      const hasGallery = document.querySelector('.product-gallery-section') !== null;
      const hasMainImage = document.getElementById('main-product-image') !== null;
      const result = hasGallery || hasMainImage;
      Utils.log(`🔍 isProductPage check: gallery=${hasGallery}, mainImage=${hasMainImage}, result=${result}`);
      return result;
    }

    isCollectionPage() {
      return document.querySelector('.collection-grid') !== null;
    }

    initProductPage() {
      Utils.log('🎯 Initializing product page scripts...');

      // PROFESSIONAL FIX: Wait for both productJSON AND DOM elements
      // Uses polling with timeout to ensure reliable initialization
      const maxAttempts = 20; // 20 * 100ms = 2 seconds max wait
      let attempts = 0;

      const tryInit = () => {
        attempts++;
        Utils.log(`🔄 Init attempt ${attempts}/${maxAttempts}`);

        // Check 1: productJSON must exist
        if (!window.productJSON) {
          Utils.log('⏳ Waiting for productJSON...');
          if (attempts < maxAttempts) {
            setTimeout(tryInit, 100);
          } else {
            Utils.error('❌ productJSON not available after 2 seconds');
          }
          return;
        }

        // Check 2: Main product image must exist in DOM
        const mainImage = document.getElementById('main-product-image');
        if (!mainImage) {
          Utils.log('⏳ Waiting for DOM elements (main-product-image)...');
          if (attempts < maxAttempts) {
            setTimeout(tryInit, 100);
          } else {
            Utils.error('❌ DOM elements not found after 2 seconds');
          }
          return;
        }

        // Check 3: Thumbnails must exist
        const thumbnails = document.querySelectorAll('.thumbnail-wrapper');
        Utils.log(`📸 Found ${thumbnails.length} thumbnails`);

        // All checks passed - initialize
        try {
          if (typeof window.initProductPage === 'function') {
            Utils.log('✅ All conditions met - calling window.initProductPage()');
            window.initProductPage();
            Utils.log('✅ Product page reinitialized successfully');
          } else {
            Utils.error('❌ window.initProductPage not available');
          }
        } catch (error) {
          Utils.error('❌ Product page initialization error:', error);
        }
      };

      // Start initialization after RAF + small delay for DOM stability
      requestAnimationFrame(() => {
        setTimeout(tryInit, 50);
      });
    }

    initCollectionPage() {
      Utils.log('Initializing collection page scripts...');

      // Collection page обычно не требует специальной инициализации
      // Все фильтры/сортировка работают через Shopify forms
    }

    executeInlineScripts() {
      // Находим все <script> в новом main
      const main = document.querySelector('main');
      const scripts = main.querySelectorAll('script');

      Utils.log(`🔧 Found ${scripts.length} scripts in main content`);

      scripts.forEach((script, index) => {
        if (script.src) {
          // External script - не выполняем (уже загружены глобально)
          Utils.log(`📦 Script ${index + 1}: External (skipped)`);
          return;
        }

        // Inline script - выполняем СИНХРОННО через eval
        try {
          const scriptContent = script.textContent;
          if (scriptContent.trim()) {
            // Check if this script sets productJSON (critical for gallery)
            const setsProductJSON = scriptContent.includes('productJSON');

            if (setsProductJSON) {
              Utils.log(`🎯 Script ${index + 1}: Contains productJSON - executing synchronously`);
            } else {
              Utils.log(`📝 Script ${index + 1}: Inline script - executing`);
            }

            // Use Function constructor for synchronous execution
            // This is safer than eval and executes immediately
            const executableScript = new Function(scriptContent);
            executableScript();

            if (setsProductJSON) {
              Utils.log(`✅ productJSON now set:`, !!window.productJSON);
              if (window.productJSON) {
                Utils.log(`   Product: ${window.productJSON.title}`);
              }
            }
          }
        } catch (error) {
          Utils.error(`❌ Failed to execute inline script ${index + 1}:`, error);
        }
      });

      Utils.log('✅ All inline scripts executed');
    }

    triggerDOMContentLoaded() {
      // OPTIMIZED: Use custom event instead of DOMContentLoaded
      // This prevents duplicate event listeners from accumulating
      // Page-specific init functions are already called above in reinitialize()

      // Dispatch custom event for SPA-aware scripts
      const spaEvent = new CustomEvent('spa:contentLoaded', {
        bubbles: true,
        cancelable: false,
        detail: { timestamp: Date.now() }
      });
      document.dispatchEvent(spaEvent);

      Utils.log('SPA content loaded event dispatched');
    }
  }

  // ========================================
  // HISTORY MANAGER
  // ========================================
  class HistoryManager {
    constructor(onPopState) {
      this.onPopState = onPopState;
      this.init();
    }

    init() {
      // Disable browser's automatic scroll restoration - SPA handles it
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      window.addEventListener('popstate', this.handlePopState.bind(this));

      // Save initial state
      history.replaceState({
        url: window.location.href,
        title: document.title,
        scrollY: window.scrollY
      }, document.title, window.location.href);

      Utils.log('History manager initialized');
    }

    pushState(url, title) {
      const state = {
        url,
        title,
        scrollY: window.scrollY,
        timestamp: Date.now()
      };

      history.pushState(state, title, url);
      Utils.log('History state pushed:', url);
    }

    handlePopState(e) {
      Utils.log('Popstate event:', e.state);

      // Debounce for mobile - prevent rapid popstate events
      if (this._popstateTimeout) {
        clearTimeout(this._popstateTimeout);
      }

      this._popstateTimeout = setTimeout(() => {
        if (e.state && e.state.url) {
          // Navigate to previous page
          this.onPopState(e.state.url, e.state);
        } else {
          // No state - check if current URL is internal (same origin)
          const currentUrl = window.location.href;
          const isInternal = currentUrl.includes(window.location.host);

          if (isInternal) {
            // Internal page without state (e.g., initial page or mobile quirk)
            // Use SPA to load current URL instead of browser reload
            Utils.log('No SPA state but internal URL - using SPA navigation');
            this.onPopState(currentUrl, null);
          } else {
            // External page - let browser handle
            Utils.log('No SPA state - allowing native browser navigation');
          }
        }
      }, 50); // 50ms debounce for mobile
    }
  }

  // ========================================
  // TRANSITION MANAGER
  // ========================================
  class TransitionManager {
    constructor() {
      this.duration = SPA_CONFIG.transitionDuration;
    }

    async fadeOut(element) {
      if (!element) return;

      element.style.transition = `opacity ${this.duration}ms ease-out`;
      element.style.opacity = '0';

      await Utils.delay(this.duration);
      Utils.log('Fade out completed');
    }

    async fadeIn(element) {
      if (!element) return;

      // Force reflow
      element.offsetHeight;

      element.style.transition = `opacity ${this.duration}ms ease-in`;
      element.style.opacity = '1';

      await Utils.delay(this.duration);
      Utils.log('Fade in completed');
    }

    addTransitioningClass() {
      document.body.classList.add('transitioning');
    }

    removeTransitioningClass() {
      document.body.classList.remove('transitioning');
    }
  }

  // ========================================
  // ANALYTICS TRACKER
  // ========================================
  class AnalyticsTracker {
    trackPageView(url, title) {
      try {
        // Shopify Analytics
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
          window.ShopifyAnalytics.lib.page();
          Utils.log('Shopify Analytics pageview tracked');
        }

        // Google Analytics (если установлен)
        if (window.gtag) {
          window.gtag('config', window.GA_MEASUREMENT_ID, {
            page_path: new URL(url).pathname
          });
          Utils.log('Google Analytics pageview tracked');
        }

        // Facebook Pixel (если установлен)
        if (window.fbq) {
          window.fbq('track', 'PageView');
          Utils.log('Facebook Pixel pageview tracked');
        }

      } catch (error) {
        Utils.error('Analytics tracking error:', error);
      }
    }
  }

  // ========================================
  // MAIN HYBRID SPA CONTROLLER
  // ========================================
  class HybridSPA {
    constructor() {
      // Проверка поддержки браузером
      if (!this.isSupported()) {
        Utils.log('Browser not supported, using traditional navigation');
        return;
      }

      // Initialize components
      this.stateManager = new SPAStateManager();
      this.pageLoader = new PageLoader(this.stateManager);
      this.contentReplacer = new ContentReplacer();
      this.scriptReinitializer = new ScriptReinitializer();
      this.transitionManager = new TransitionManager();
      this.analyticsTracker = new AnalyticsTracker();

      this.historyManager = new HistoryManager(this.handlePopState.bind(this));
      this.linkInterceptor = new LinkInterceptor(
        this.navigate.bind(this),
        this.prefetch.bind(this)
      );

      this.init();
    }

    isSupported() {
      return !!(
        window.history &&
        window.history.pushState &&
        window.DOMParser &&
        window.fetch
      );
    }

    init() {
      // Mark body as SPA-active
      document.body.classList.add('spa-active');

      // Listen for custom events
      this.setupEventListeners();

      Utils.log('✅ Hybrid SPA system initialized and ready!');
      Utils.log('🎵 Music player will continue playing during navigation');
      Utils.dispatchEvent('spa:ready');
    }

    setupEventListeners() {
      // Listen for forms submission (например filters)
      document.addEventListener('submit', (e) => {
        const form = e.target;

        // Shopify collection filters обычно используют GET forms
        if (form.method.toLowerCase() === 'get') {
          e.preventDefault();

          const formData = new FormData(form);
          const params = new URLSearchParams(formData);
          const url = `${form.action}?${params.toString()}`;

          this.navigate(url);
        }
      });
    }

    async navigate(url, isPopState = false, popStateData = null) {
      try {
        // Prevent navigation if already transitioning
        if (this.stateManager.isTransitioning) {
          Utils.log('Navigation blocked - already transitioning');
          return;
        }

        // Check if target URL is excluded path - do full page reload
        // This fixes back button issues when navigating from/to excluded pages like /cart
        if (Utils.isExcludedPath(url)) {
          Utils.log('🔄 Target is excluded path - doing full reload:', url);
          window.location.href = url;
          return;
        }

        // ВАЖНО: Нормализуем URLs для сравнения
        const normalizeUrl = (urlString) => {
          try {
            const urlObj = new URL(urlString, window.location.origin);
            // Убираем hash и trailing slash
            return urlObj.origin + urlObj.pathname.replace(/\/$/, '') + urlObj.search;
          } catch (e) {
            return urlString;
          }
        };

        const currentUrl = normalizeUrl(window.location.href);
        const targetUrl = normalizeUrl(url);

        // Проверка: если это тот же URL - не делаем navigation
        // НО: при popstate браузер уже изменил URL, поэтому нужно загрузить контент
        if (currentUrl === targetUrl && !isPopState) {
          Utils.log('⚠️ Same URL detected, skipping navigation:', url);
          return;
        }

        Utils.log('🚀 Starting navigation to:', url);
        Utils.log('   From:', currentUrl);
        Utils.log('   To:', targetUrl);
        Utils.log('   Is popstate:', isPopState);

        this.stateManager.isTransitioning = true;
        this.transitionManager.addTransitioningClass();

        Utils.dispatchEvent('spa:navigationStart', { url, isPopState });

        // 1. Save current scroll position
        if (!isPopState) {
          this.stateManager.saveScrollPosition(window.location.href);
        }

        // 2. Fade out current content
        const main = document.querySelector('main');
        await this.transitionManager.fadeOut(main);

        // 3. Fetch new page
        const newDoc = await this.pageLoader.fetchPage(url);

        if (!newDoc) {
          // Fetch was aborted or failed
          throw new Error('Failed to load page');
        }

        // 4. Replace content
        this.contentReplacer.replaceContent(newDoc);

        // 5. Update browser history (если не popstate)
        if (!isPopState) {
          this.historyManager.pushState(url, newDoc.title);
        }

        // 6. Update current URL
        this.stateManager.currentUrl = url;

        // 7. Scroll management
        if (isPopState && popStateData) {
          // Restore scroll position from history
          this.stateManager.restoreScrollPosition(url);
        } else {
          // Scroll to top
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: SPA_CONFIG.scrollBehavior
          });
        }

        // 8. Reinitialize scripts
        this.scriptReinitializer.reinitialize(newDoc);

        // 9. Fade in new content
        const newMain = document.querySelector('main');
        await this.transitionManager.fadeIn(newMain);

        // 10. Track analytics
        this.analyticsTracker.trackPageView(url, newDoc.title);

        // 11. Clean up
        this.stateManager.clearScrollPositions();
        this.stateManager.isTransitioning = false;
        this.transitionManager.removeTransitioningClass();

        Utils.log('✅ Navigation completed successfully');
        Utils.dispatchEvent('spa:navigationEnd', { url });

      } catch (error) {
        Utils.error('Navigation failed:', error);

        // Fallback - full page reload
        Utils.log('Falling back to full page reload');
        window.location.href = url;
      }
    }

    async handlePopState(url, state) {
      Utils.log('Handling popstate for:', url);
      await this.navigate(url, true, state);
    }

    prefetch(url) {
      // Prefetch page in background
      this.pageLoader.prefetchPage(url);
    }
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHybridSPA);
  } else {
    initHybridSPA();
  }

  function initHybridSPA() {
    try {
      window.hybridSPA = new HybridSPA();
    } catch (error) {
      Utils.error('Failed to initialize Hybrid SPA:', error);
      // Fallback - традиционная навигация
    }
  }

  // ========================================
  // CSS ANIMATIONS
  // ========================================

  // Добавляем CSS для transitions
  const style = document.createElement('style');
  style.textContent = `
    /* Hybrid SPA Transitions */
    body.transitioning {
      pointer-events: none;
    }

    main {
      transition: opacity 300ms ease-in-out;
    }

    /* Loading indicator */
    body.transitioning::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, #FF1493, #00FFFF, #8A2BE2, #39FF14);
      background-size: 200% 100%;
      animation: loading-bar 1s ease-in-out infinite;
      z-index: 10001;
    }

    @keyframes loading-bar {
      0% { background-position: 0% 0%; }
      100% { background-position: 200% 0%; }
    }

    /* Smooth page transitions */
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;
  document.head.appendChild(style);

})();
